import React, { useEffect, useState } from "react";
import {
  Calendar,
  Users,
  Clock,
  Filter,
  CheckCircle,
  LogIn,
  LogOut,
  Search,
  AlertCircle,
  CalendarDays,
  QrCode,
} from "lucide-react";
import { llegadaDe } from "../utils/llegada";
import { coincide, coincidePlaca } from "../utils/texto";
import { hoySV, mostrarDia } from "../utils/fecha";
import api from "../api/axios";
import VisitForm from "./VisitForm";
import VisitCalendar from "./VisitCalendar";
import QRScanner from "./QRScanner";
import Header from "./Header";
import socketManager from "../api/socket";
import { Card } from "./shared/Card";
import { Button } from "./shared/Button";
import { StatusBadge } from "./shared/Badge";
import { Table } from "./shared/Table";
import { DatePicker } from "./shared/DatePicker";
import Navegador from "./modulos/Navegador";

const styles = {
  container: {
    minHeight: "100vh",
    backgroundColor: "var(--bg)",
  },
  content: {
    maxWidth: "var(--container-max-width)",
    margin: "0 auto",
    padding: "var(--space-10) var(--container-padding)",
  },
  header: {
    marginBottom: "var(--space-12)",
  },
  title: {
    fontFamily: "var(--font-display)",
    fontSize: "var(--text-4xl)",
    fontWeight: "700",
    color: "var(--text)",
    marginBottom: "0.75rem",
    letterSpacing: "var(--tracking-tight)",
  },
  subtitle: {
    fontFamily: "var(--font-body)",
    fontSize: "var(--text-base)",
    color: "var(--text-secondary)",
    letterSpacing: "var(--tracking-wide)",
  },
  stats: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(min(280px, 100%), 1fr))",
    gap: "var(--space-6)",
    marginBottom: "var(--space-12)",
  },
  statCard: {
    padding: "var(--space-8)",
    display: "flex",
    alignItems: "center",
    gap: "var(--space-6)",
  },
  statIcon: {
    width: "56px",
    height: "56px",
    borderRadius: "var(--radius-lg)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    position: "relative",
    overflow: "hidden",
  },
  statIconShimmer: {
    position: "absolute",
    top: 0,
    left: "-100%",
    width: "100%",
    height: "100%",
    background:
      "linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.3) 50%, transparent 100%)",
    animation: "shimmer 3s infinite",
  },
  statContent: {
    flex: 1,
  },
  statValue: {
    fontFamily: "var(--font-display)",
    fontSize: "var(--text-3xl)",
    fontWeight: "700",
    color: "var(--text)",
    letterSpacing: "var(--tracking-tight)",
  },
  statLabel: {
    fontFamily: "var(--font-body)",
    fontSize: "var(--text-sm)",
    color: "var(--text-tertiary)",
    letterSpacing: "var(--tracking-wide)",
    textTransform: "uppercase",
    marginTop: "0.25rem",
  },
  controls: {
    display: "flex",
    gap: "var(--space-4)",
    marginBottom: "var(--space-6)",
    flexWrap: "wrap",
    alignItems: "center",
  },
  dateInput: {
    padding: "0.75rem 1.25rem",
    borderRadius: "var(--radius-lg)",
    border: "2px solid var(--border)",
    backgroundColor: "var(--bg-tertiary)",
    fontSize: "var(--text-sm)",
    fontFamily: "var(--font-body)",
    fontWeight: "500",
    color: "var(--text)",
    cursor: "pointer",
    transition: "all 0.2s ease",
    outline: "none",
  },
  dateInputFocus: {
    borderColor: "var(--gold)",
    backgroundColor: "var(--bg-secondary)",
  },
  searchContainer: {
    position: "relative",
    flex: "1 1 300px",
    minWidth: "0",
    maxWidth: "400px",
  },
  searchIcon: {
    position: "absolute",
    left: "1rem",
    top: "50%",
    transform: "translateY(-50%)",
    color: "var(--text-muted)",
    transition: "color 0.2s ease",
  },
  searchIconFocus: {
    color: "var(--gold)",
  },
  searchInput: {
    width: "100%",
    padding: "var(--space-3) var(--space-5) var(--space-3) 3rem",
    borderRadius: "var(--radius-lg)",
    border: "2px solid var(--border)",
    backgroundColor: "var(--bg-tertiary)",
    fontSize: "var(--text-sm)",
    fontFamily: "var(--font-body)",
    color: "var(--text)",
    transition: "all 0.2s ease",
    outline: "none",
    minHeight: "44px",
  },
  searchInputFocus: {
    borderColor: "var(--gold)",
    backgroundColor: "var(--bg-secondary)",
  },
  emptyState: {
    textAlign: "center",
    padding: "var(--space-16) var(--space-4)",
    color: "var(--text-tertiary)",
  },
  emptyIcon: {
    width: "64px",
    height: "64px",
    margin: "0 auto 1.5rem",
    color: "var(--text-muted)",
  },
  emptyTitle: {
    fontFamily: "var(--font-display)",
    fontSize: "var(--text-xl)",
    fontWeight: "600",
    color: "var(--text-secondary)",
    marginBottom: "0.5rem",
  },
  emptyText: {
    fontFamily: "var(--font-body)",
    fontSize: "var(--text-base)",
    color: "var(--text-tertiary)",
  },
  addVisitSection: {
    marginBottom: "2.5rem",
  },
  toggleButton: {
    marginBottom: "1.5rem",
  },
  loadingSpinner: {
    width: "3rem",
    height: "3rem",
    border: "3px solid var(--border)",
    borderTopColor: "var(--gold)",
    borderRadius: "50%",
    margin: "0 auto",
    // `linear`: una rueda que gira sin parar tiene que girar a velocidad
    // constante. La curva elástica que había aquí la hacía acelerar y pasarse
    // en cada vuelta, y un indicador de carga que da tirones se lee como que
    // la aplicación está trabada, no como que está cargando.
    animation: "spin 1s linear infinite",
  },
  viewTabs: {
    display: "flex",
    gap: "var(--space-2)",
    marginBottom: "var(--space-8)",
    backgroundColor: "var(--bg-elevated)",
    padding: "0.375rem",
    borderRadius: "var(--radius-lg)",
    border: "1px solid var(--border)",
    width: "fit-content",
    flexWrap: "wrap",
  },
  viewTab: {
    padding: "var(--space-3) var(--space-6)",
    fontSize: "var(--text-sm)",
    fontWeight: "600",
    backgroundColor: "transparent",
    border: "none",
    borderRadius: "var(--radius-md)",
    color: "var(--text-secondary)",
    cursor: "pointer",
    transition: "all 0.2s ease",
    display: "flex",
    alignItems: "center",
    gap: "var(--space-2)",
    whiteSpace: "nowrap",
  },
  viewTabActive: {
    backgroundColor: "var(--gold)",
    color: "white",
    boxShadow: "var(--shadow-sm)",
  },
};

export default function GuardDashboard() {
  const [visits, setVisits] = useState([]);
  /* HOY EN EL SALVADOR, no en UTC. `toISOString()` devuelve UTC, así que a
     partir de las 6 de la tarde el panel abría mostrando MAÑANA y las
     visitas del turno desaparecían — en las horas de más movimiento. */
  const [selectedDate, setSelectedDate] = useState(hoySV());
  const [loading, setLoading] = useState(true);
  /* «No hay visitas» y «no se pudo cargar» son dos respuestas muy distintas,
   * y la segunda pide llamar a alguien. Sin este estado, un fallo de red le
   * decía al guardia que su turno no tenía visitas. */
  const [errorCarga, setErrorCarga] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showAddForm, setShowAddForm] = useState(false);
  const [dateInputFocused, setDateInputFocused] = useState(false);
  const [searchInputFocused, setSearchInputFocused] = useState(false);
  const [currentView, setCurrentView] = useState("daily"); // "daily" or "calendar"
  const [showQRScanner, setShowQRScanner] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    entered: 0,
    exited: 0,
  });

  const fetchVisits = async () => {
    setErrorCarga(null);
    setLoading(true);
    try {
      const res = await api.get(`/visits/by-date?date=${selectedDate}`);
      const visitList = res.data.visits || res.data || [];

      // Backend now handles timezone correctly
      setVisits(visitList);

      // Calculate stats
      const newStats = visitList.reduce(
        (acc, visit) => {
          acc.total++;
          /* «Entraron» cuenta a TODO el que entró hoy, haya salido o no.
           *
           * Antes la cadena `else if` sólo sumaba `Entered` —quien sigue
           * dentro— bajo esa etiqueta, así que el tablero llegaba a decir
           * «2 ENTRARON · 4 SALIERON»: más gente saliendo de la que entró.
           * Y la pestaña «Ahora» del mismo panel decía 6 y 4, porque la
           * cuenta de verdad la hace el servidor.
           *
           * La semántica correcta ya estaba escrita y comentada en
           * `VisitSummary.jsx`; aquí se había quedado a medias. */
          if (visit.status === "Pending") acc.pending++;
          if (visit.status === "Entered" || visit.status === "Exited") acc.entered++;
          if (visit.status === "Exited") acc.exited++;
          return acc;
        },
        { total: 0, pending: 0, entered: 0, exited: 0 }
      );

      setStats(newStats);
    } catch (err) {
      console.error("Error al cargar visitas", err);
      setVisits([]);
      setErrorCarga(
        err?.response?.data?.message ||
          "No se pudieron cargar las visitas. Revise la conexión."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVisits();

    const socket = socketManager.get();
    socket.emit("watchDate", selectedDate);

    socket.on("visitCreated", (newVisit) => {
      // Backend should only send visits for the watched date
      setVisits((prev) => {
        const exists = prev.some((v) => v._id === newVisit._id);
        return exists ? prev : [...prev, newVisit];
      });
    });

    socket.on("visitUpdated", (updatedVisit) => {
      setVisits((prev) =>
        prev.map((v) => (v._id === updatedVisit._id ? updatedVisit : v))
      );
    });

    return () => {
      socket.off("visitCreated");
      socket.off("visitUpdated");
    };
  }, [selectedDate]);

  const marcarEntrada = async (id) => {
    try {
      await api.patch(`/visits/${id}/enter`, {});
      fetchVisits();
    } catch (err) {
      console.error("Error al marcar entrada", err);
    }
  };

  const marcarSalida = async (id) => {
    try {
      await api.patch(`/visits/${id}/exit`, {});
      fetchVisits();
    } catch (err) {
      console.error("Error al marcar salida", err);
    }
  };

  const filteredVisits = visits.filter(
    (visit) =>
      coincide(visit.visitorName, searchTerm) ||
      coincide(visit.houseNumber, searchTerm) ||
      coincide(visit.polygon, searchTerm) ||
      // Por la placa también: cuando el carro ya pasó, es lo único que queda.
      // Con `coincidePlaca`, no con `coincide`: la placa se guarda sin guiones,
      // así que un guardia que teclee «P123-456» tiene que encontrarla igual.
      coincidePlaca(visit.plate, searchTerm)
  );

  const columns = [
    {
      key: "visitorName",
      label: "Visitante",
      width: "22%",
      render: (value) => (
        <div style={{ fontWeight: "600", color: "var(--text)" }}>{value}</div>
      ),
    },
    {
      key: "location",
      label: "Destino",
      width: "12%",
      render: (_, row) => (
        <div
          style={{
            fontWeight: "600",
            color: "var(--gold-dark)",
            fontSize: "0.875rem",
          }}
        >
          {row.polygon} - {row.houseNumber}
        </div>
      ),
    },
    {
      /* Cómo llega, y en qué. Va junto al destino porque es lo que el guardia
         mira a la vez: a qué casa va y qué tiene delante. */
      key: "arrivalMode",
      label: "Llega",
      width: "14%",
      render: (_, row) => {
        const { corta, icono: Icono } = llegadaDe(row);
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
            <Icono size={16} style={{ color: "var(--text-tertiary)", flexShrink: 0 }} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: "0.8125rem", color: "var(--text)" }}>{corta}</div>
              {row.plate && (
                <div
                  style={{
                    fontSize: "0.8125rem",
                    fontWeight: "700",
                    letterSpacing: "0.06em",
                    color: "var(--gold-dark)",
                  }}
                >
                  {row.plate}
                </div>
              )}
            </div>
          </div>
        );
      },
    },
    {
      key: "date",
      label: "Fecha de visita",
      width: "18%",
      render: (value) => {
        return (
          <div style={{ fontSize: "0.875rem" }}>
            <div style={{ fontWeight: "600", color: "var(--text)" }}>
              {/* `mostrarDia`: la fecha llega como medianoche UTC y formatearla
                  en hora de El Salvador enseñaba el día anterior, con el
                  guardia buscando en la caseta una visita que el panel ponía
                  ayer. */}
              {mostrarDia(value, {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })}
            </div>
            {/* <div style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
              {new Date(value).toLocaleTimeString("es-SV", {
                timeZone: "America/El_Salvador",
                timeStyle: "short",
              })}
            </div> */}
          </div>
        );
      },
    },
    {
      key: "status",
      label: "Estado",
      width: "12%",
      render: (value) => <StatusBadge status={value} size="sm" />,
    },
    {
      key: "entryTime",
      label: "Registro",
      width: "18%",
      render: (value, row) => (
        <div style={{ fontSize: "0.75rem" }}>
          {row.entryTime && (
            <div
              style={{
                color: "var(--success)",
                display: "flex",
                alignItems: "center",
                gap: "0.25rem",
              }}
            >
              <LogIn size={12} />
              {new Date(row.entryTime).toLocaleString("es-SV", {
                timeZone: "America/El_Salvador",
                timeStyle: "short",
              })}
            </div>
          )}
          {row.exitTime && (
            <div
              style={{
                color: "var(--info)",
                display: "flex",
                alignItems: "center",
                gap: "0.25rem",
                marginTop: "0.125rem",
              }}
            >
              <LogOut size={12} />
              {new Date(row.exitTime).toLocaleString("es-SV", {
                timeZone: "America/El_Salvador",
                timeStyle: "short",
              })}
            </div>
          )}
          {!row.entryTime && !row.exitTime && (
            <span style={{ color: "var(--text-muted)" }}>-</span>
          )}
        </div>
      ),
    },
    {
      key: "actions",
      label: "Acción",
      width: "18%",
      align: "center",
      render: (_, visit) => (
        <div style={{ display: "flex", justifyContent: "center" }}>
          {visit.status === "Pending" && (
            <Button
              size="sm"
              variant="primary"
              onClick={() => marcarEntrada(visit._id)}
              leftIcon={LogIn}
              style={{ fontSize: "0.75rem", padding: "0.5rem 0.875rem" }}
            >
              Entrada
            </Button>
          )}
          {visit.status === "Entered" && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => marcarSalida(visit._id)}
              leftIcon={LogOut}
              style={{ fontSize: "0.75rem", padding: "0.5rem 0.875rem" }}
            >
              Salida
            </Button>
          )}
          {visit.status === "Exited" && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.25rem",
                color: "var(--success)",
              }}
            >
              <CheckCircle size={16} />
              <span style={{ fontSize: "0.75rem" }}>Completo</span>
            </div>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <Header />
      <div style={styles.container}>
        <div style={styles.content}>
          <div style={styles.header}>
            <h1 style={styles.title}>Panel de Control - Guardia</h1>
            <p style={styles.subtitle}>
              Gestiona las visitas programadas para hoy y controla el acceso a
              la residencial
            </p>
          </div>

          <div style={styles.stats}>
            <Card hoverable luxuryAccent style={styles.statCard}>
              <div
                style={{
                  ...styles.statIcon,
                  background: "var(--gradient-gold)",
                  color: "white",
                }}
              >
                <Users size={28} />
                <div style={styles.statIconShimmer} />
              </div>
              <div style={styles.statContent}>
                <div style={styles.statValue}>{stats.total}</div>
                <div style={styles.statLabel}>Total Visitas</div>
              </div>
            </Card>

            <Card hoverable luxuryAccent style={styles.statCard}>
              <div
                style={{
                  ...styles.statIcon,
                  background: "var(--warning-bg)",
                  color: "var(--warning-text)",
                }}
              >
                <Clock size={28} />
              </div>
              <div style={styles.statContent}>
                <div style={styles.statValue}>{stats.pending}</div>
                <div style={styles.statLabel}>Pendientes</div>
              </div>
            </Card>

            <Card hoverable luxuryAccent style={styles.statCard}>
              <div
                style={{
                  ...styles.statIcon,
                  background: "var(--success-bg)",
                  color: "var(--success-text)",
                }}
              >
                <LogIn size={28} />
              </div>
              <div style={styles.statContent}>
                <div style={styles.statValue}>{stats.entered}</div>
                <div style={styles.statLabel}>Entraron</div>
              </div>
            </Card>

            <Card hoverable luxuryAccent style={styles.statCard}>
              <div
                style={{
                  ...styles.statIcon,
                  background: "var(--info-bg)",
                  color: "var(--info-text)",
                }}
              >
                <LogOut size={28} />
              </div>
              <div style={styles.statContent}>
                <div style={styles.statValue}>{stats.exited}</div>
                <div style={styles.statLabel}>Salieron</div>
              </div>
            </Card>
          </div>

          <div style={styles.viewTabs}>
            <button
              onClick={() => setCurrentView("daily")}
              style={{
                ...styles.viewTab,
                ...(currentView === "daily" ? styles.viewTabActive : {}),
              }}
            >
              <Calendar size={18} />
              Vista Diaria
            </button>
            <button
              onClick={() => setCurrentView("calendar")}
              style={{
                ...styles.viewTab,
                ...(currentView === "calendar" ? styles.viewTabActive : {}),
              }}
            >
              <CalendarDays size={18} />
              Calendario
            </button>
          </div>

          {currentView === "daily" ? (
            <>
              <div style={styles.addVisitSection}>
                <Button
                  onClick={() => setShowAddForm(!showAddForm)}
                  leftIcon={Users}
                  variant="primary"
                  size="md"
                  style={styles.toggleButton}
                >
                  {showAddForm ? "Ocultar formulario" : "Registrar nueva visita"}
                </Button>

                {showAddForm && (
                  <Card luxuryAccent padding="lg" overflowVisible>
                    <VisitForm
                      onVisitCreated={() => {
                        fetchVisits();
                        setShowAddForm(false);
                      }}
                    />
                  </Card>
                )}
              </div>

              <Card luxuryAccent padding="lg" overflowVisible>
            <div style={styles.controls}>
              <div style={{ width: "100%", maxWidth: "200px" }}>
                <DatePicker
                  value={selectedDate}
                  onChange={setSelectedDate}
                  placeholder="Seleccionar fecha"
                />
              </div>

              <div style={styles.searchContainer}>
                <Search
                  size={18}
                  style={{
                    ...styles.searchIcon,
                    ...(searchInputFocused ? styles.searchIconFocus : {}),
                  }}
                />
                <input
                  type="text"
                  placeholder="Buscar por visitante, casa, polígono o placa..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onFocus={() => setSearchInputFocused(true)}
                  onBlur={() => setSearchInputFocused(false)}
                  style={{
                    ...styles.searchInput,
                    ...(searchInputFocused ? styles.searchInputFocus : {}),
                  }}
                />
              </div>

              <div style={{ marginLeft: "auto", display: "flex", gap: "var(--space-3)", flexWrap: "wrap" }}>
                <Button 
                  variant="primary" 
                  leftIcon={QrCode} 
                  size="md"
                  onClick={() => setShowQRScanner(true)}
                >
                  Escanear QR
                </Button>
                <Button variant="secondary" leftIcon={Filter} size="md">
                  Filtros
                </Button>
              </div>
            </div>

            {loading ? (
              <div style={styles.emptyState}>
                <div
                  className="animate-spin"
                  style={styles.loadingSpinner}
                ></div>
                <p style={{ ...styles.emptyText, marginTop: "1.5rem" }}>
                  Cargando visitas...
                </p>
              </div>
            ) : errorCarga ? (
              <div style={styles.emptyState}>
                <AlertCircle style={{ ...styles.emptyIcon, color: "var(--danger)" }} />
                <h3 style={{ ...styles.emptyTitle, color: "var(--danger)" }}>
                  No se pudo cargar
                </h3>
                <p style={styles.emptyText}>{errorCarga}</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={fetchVisits}
                  style={{ marginTop: "1rem" }}
                >
                  Reintentar
                </Button>
              </div>
            ) : filteredVisits.length === 0 ? (
              <div style={styles.emptyState}>
                <AlertCircle style={styles.emptyIcon} />
                <h3 style={styles.emptyTitle}>No hay visitas</h3>
                <p style={styles.emptyText}>
                  {searchTerm
                    ? "No se encontraron visitas con ese criterio"
                    : "No hay visitas programadas para esta fecha"}
                </p>
              </div>
            ) : (
              <div className="responsive-table">
                <Table
                  columns={columns}
                  data={filteredVisits}
                  hoverable
                  luxuryAccent
                  highlightColumns={["visitorName"]}
                />
              </div>
            )}
          </Card>
            </>
          ) : (
            <VisitCalendar />
          )}
          
          {showQRScanner && (
            <QRScanner
              onClose={() => setShowQRScanner(false)}
              onScanSuccess={() => {
                setShowQRScanner(false);
                fetchVisits(); // Recargar visitas después de escanear
              }}
            />
          )}

          {/* Los módulos que se añadieron con el roadmap. Van debajo de lo
              que ya existía: quien usa esto a diario no debería tener que
              buscar de nuevo lo que hace todos los días. */}
          <div style={{ marginTop: "var(--space-8)" }}>
            <Navegador rol="guard" />
          </div>
        </div>
      </div>
    </>
  );
}
